import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { FiMapPin, FiCalendar, FiUser, FiSearch, FiX } from "react-icons/fi";
import "react-datepicker/dist/react-datepicker.css";
import "./searchbar.css";

// Reintroduce the hardcoded cities list
const cities = [
    "New York, USA", "Los Angeles, USA", "Chicago, USA", "Houston, USA", "Miami, USA",
    "London, UK", "Manchester, UK", "Birmingham, UK", "Liverpool, UK", "Edinburgh, UK",
    "Paris, France", "Marseille, France", "Lyon, France", "Toulouse, France", "Nice, France",
    "Berlin, Germany", "Munich, Germany", "Frankfurt, Germany", "Hamburg, Germany", "Cologne, Germany",
    "Rome, Italy", "Milan, Italy", "Naples, Italy", "Turin, Italy", "Palermo, Italy",
    "Madrid, Spain", "Barcelona, Spain", "Valencia, Spain", "Seville, Spain", "Zaragoza, Spain",
    "Sydney, Australia", "Melbourne, Australia", "Brisbane, Australia", "Perth, Australia", "Adelaide, Australia",
    "Toronto, Canada", "Vancouver, Canada", "Montreal, Canada", "Calgary, Canada", "Ottawa, Canada",
    "Tokyo, Japan", "Osaka, Japan", "Kyoto, Japan", "Nagoya, Japan", "Sapporo, Japan"
];

// זיהוי מובייל רספונסיבי
function useIsMobile(breakpoint = 800) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    useEffect(() => {
        const media = window.matchMedia(`(max-width: ${breakpoint}px)`);
        const listener = () => setIsMobile(media.matches);
        media.addEventListener('change', listener);
        setIsMobile(media.matches);
        return () => media.removeEventListener('change', listener);
    }, [breakpoint]);
    return isMobile;
}

export default function Searchbar({
                                      location, date, guests,
                                      onLocationChange, onDateChange, onGuestsChange,
                                      onSearch, onClearSearch,
                                      availableLocations
                                  }) {
    const [open, setOpen] = useState("");
    const [locationInput, setLocationInput] = useState(location || "");
    const [dateInput, setDateInput] = useState(date || null);
    const [guestsInput, setGuestsInput] = useState(guests || null);
    const [combinedLocations, setCombinedLocations] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);

    // Combine and manage location list
    useEffect(() => {
        const hostLocations = availableLocations || [];
        const combined = [...new Set([...hostLocations, ...cities])].sort();
        setCombinedLocations(combined);
        setFilteredLocations(combined);
    }, [availableLocations]);

    // Update internal input if external location prop changes
    useEffect(() => {
        setLocationInput(location || "");
    }, [location]);
    useEffect(() => {
        setDateInput(date || null);
    }, [date]);
    useEffect(() => {
        setGuestsInput(guests || null);
    }, [guests]);

    // Filter combined locations based on internal input
    useEffect(() => {
        setFilteredLocations(
            combinedLocations.filter(loc =>
                loc.toLowerCase().includes(locationInput.toLowerCase())
            )
        );
    }, [locationInput, combinedLocations]);

    // Click outside handler
    useEffect(() => {
        function handleClickOutside(event) {
            if (!event.target.closest(".searchbar-box")) setOpen("");
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const showClearButton = location || date || guests;

    // כפתור החיפוש פעיל אם לפחות אחד מהשדות מלא
    const isSearchEnabled = !!(locationInput || dateInput || guestsInput);

    const handleSearchClick = () => {
        // אפשר לחפש גם אם רק אחד מהשדות מלא
        onLocationChange(locationInput || '');
        onDateChange(dateInput || null);
        onGuestsChange(guestsInput !== null ? Number(guestsInput) : null);
        onSearch();
    };

    // זיהוי מובייל
    const isMobile = window.innerWidth <= 600;

    return (
        <div className="searchbar-container">
            <div className="searchbar-box">
                {/* Location */}
                <div
                    className={`searchbar-field ${open === "location" ? "active" : ""}`}
                    onClick={() => setOpen(open === "location" ? "" : "location")}
                >
                    <FiMapPin className="searchbar-icon" />
                    <div className="searchbar-field-content">
                        {isMobile ? (
                            <span style={{fontSize: '13px', color: '#222'}}><b>Where</b> to join?</span>
                        ) : (
                            <span>
                                <b>Where</b> do you want to join?
                            </span>
                        )}
                        <div className={`searchbar-value ${locationInput ? 'has-value' : ''}`}>
                            {locationInput ? locationInput : ''}
                        </div>
                    </div>
                    {open === "location" && (
                        <div className="searchbar-dropdown" onClick={e => e.stopPropagation()}>
                            <input
                                type="text"
                                className="searchbar-autocomplete-input"
                                placeholder="City..."
                                value={locationInput}
                                onChange={e => setLocationInput(e.target.value)}
                                autoFocus
                            />
                            <div style={{maxHeight: "180px", overflowY: "auto"}}>
                                {filteredLocations.length === 0 && (
                                    <div className="searchbar-dropdown-item" style={{color: "#aaa"}}>No matching locations</div>
                                )}
                                {filteredLocations.map(locItem => (
                                    <div
                                        key={locItem}
                                        className="searchbar-dropdown-item"
                                        onClick={() => {
                                            setLocationInput(locItem);
                                            setOpen("");
                                        }}
                                    >
                                        {locItem}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="searchbar-divider" />
                {/* Date */}
                <div
                    className={`searchbar-field ${open === "date" ? "active" : ""}`}
                    onClick={() => setOpen(open === "date" ? "" : "date")}
                >
                    <FiCalendar className="searchbar-icon" />
                    <div className="searchbar-field-content">
                        {isMobile ? (
                            <span style={{fontSize: '13px', color: '#222'}}><b>Which</b> Friday?</span>
                        ) : (
                            <span>
                                <b>Which</b> Friday night?
                            </span>
                        )}
                        <div className={`searchbar-value ${dateInput ? 'has-value' : ''}`}>
                            {dateInput ? dateInput.toLocaleDateString('en-GB') : ''}
                        </div>
                    </div>
                    {open === "date" && (
                        <div className="searchbar-dropdown" onClick={e => e.stopPropagation()}>
                            <DatePicker
                                selected={dateInput}
                                onChange={d => {
                                    setDateInput(d);
                                    setOpen("");
                                }}
                                minDate={new Date()}
                                filterDate={(d) => d.getDay() === 5}
                                placeholderText="Select a date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                inline
                            />
                        </div>
                    )}
                </div>
                <div className="searchbar-divider" />
                {/* Guests */}
                <div
                    className={`searchbar-field ${open === "guests" ? "active" : ""}`}
                    onClick={() => setOpen(open === "guests" ? "" : "guests")}
                >
                    <FiUser className="searchbar-icon" />
                    <div className="searchbar-field-content">
                        {isMobile ? (
                            <span style={{fontSize: '13px', color: '#222'}}><b>How many</b> guests?</span>
                        ) : (
                            <span>
                                <b>How many</b> guests?
                            </span>
                        )}
                        <div className={`searchbar-value ${guestsInput ? 'has-value' : ''}`}>
                            {guestsInput ? guestsInput : ''}
                        </div>
                    </div>
                    {open === "guests" && (
                        <div className="searchbar-dropdown">
                            {[...Array(10)].map((_, i) => (
                                <div
                                    key={i + 1}
                                    className="searchbar-dropdown-item"
                                    onClick={() => {
                                        setGuestsInput(Number(i + 1));
                                        setOpen("");
                                    }}
                                >
                                    {i + 1}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {/* Clear Button */}
                {showClearButton && (
                    <button
                        className="searchbar-clear-btn"
                        title="Clear search criteria"
                        onClick={onClearSearch}
                    >
                        <FiX />
                    </button>
                )}
                {/* Search Button */}
                <button
                    className={`searchbar-btn ${!isSearchEnabled ? 'disabled' : ''}`}
                    title="Search"
                    onClick={handleSearchClick}
                    disabled={!isSearchEnabled}
                >
                    <FiSearch />
                </button>
            </div>
        </div>
    );
}